/*
 * Copyright 2020 Spotify AB
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createApiRef, } from "@backstage/core-plugin-api";
import { SNYK_ANNOTATION_TARGETID, SNYK_ANNOTATION_TARGETNAME, SNYK_ANNOTATION_TARGETS, SNYK_ANNOTATION_PROJECTIDS, SNYK_ANNOTATION_EXCLUDE_PROJECTIDS } from "../config";
const DEFAULT_PROXY_PATH_BASE = "";
//@ts-ignore
export const snykApiRef = createApiRef({
    id: "plugin.snyk.service",
});
export class SnykApiClient {
    constructor(options) {
        var _a;
        this.headers = {
            "Content-Type": "application/json",
            "User-Agent": "tech-services/backstage-plugin/1.0",
        };
        this.discoveryApi = options.discoveryApi;
        this.configApiRef = options.configApiRef;
        this.proxyPathBase = (_a = options.proxyPathBase) !== null && _a !== void 0 ? _a : DEFAULT_PROXY_PATH_BASE;
    }
    async getApiUrl() {
        const baseUrl = await this.discoveryApi.getBaseUrl("proxy");
        return `${baseUrl}${this.proxyPathBase}/snyk`;
    }
    GetSnykAppHost() {
        var _a;
        return (_a = this.configApiRef.getOptionalString("snyk.AppHost")) !== null && _a !== void 0 ? _a : "app.snyk.io";
    }
    async ListAllAggregatedIssues(orgId, projectId) {
        const backendBaseUrl = await this.getApiUrl();
        let v3Headers = this.headers;
        v3Headers["Content-Type"] = "application/vnd.api+json";
        const apiUrl = `${backendBaseUrl}/rest/orgs/${orgId}/issues?version=2023-06-19~experimental&scan_item.id=${projectId}&scan_item.type=project&limit=100`;
        const response = await fetch(`${apiUrl}`, {
            method: "GET",
            headers: v3Headers
        });
        if (response.status >= 400 && response.status < 600) {
            throw new Error(`Error ${response.status} - Failed fetching Vuln Issues snyk data`);
        }
        const jsonResponse = await response.json();
        return jsonResponse;
    }
    async ProjectDetails(orgName, projectId) {
        const backendBaseUrl = await this.getApiUrl();
        const apiUrl = `${backendBaseUrl}/v1/org/${orgName}/project/${projectId}`;
        const response = await fetch(`${apiUrl}`, {
            headers: this.headers,
        });
        if (response.status >= 400 && response.status < 600) {
            throw new Error(`Error ${response.status} - Failed fetching ProjectDetails snyk data`);
        }
        const jsonResponse = await response.json();
        return jsonResponse;
    }
    async GetOrgSlug(orgId) {
        const backendBaseUrl = await this.getApiUrl();
        let v3Headers = this.headers;
        v3Headers["Content-Type"] = "application/vnd.api+json";
        const orgsAPIUrl = `${backendBaseUrl}/rest/orgs/${orgId}?version=2023-06-19~beta`;
        const orgResponse = await fetch(`${orgsAPIUrl}`, {
            method: "GET",
            headers: v3Headers,
        });
        if (orgResponse.status >= 400 && orgResponse.status < 600) {
            throw new Error(`Error ${orgResponse.status} - Failed fetching Org data`);
        }
        const orgResponseData = await orgResponse.json();
        const orgData = orgResponseData.data;
        return orgData.attributes.slug;
    }
    async GetCompleteProjectsListFromAnnotations(orgId, annotations) {
        let completeProjectsList = [];
        const targetsArray = (annotations === null || annotations === void 0 ? void 0 : annotations[SNYK_ANNOTATION_TARGETS]) ? annotations === null || annotations === void 0 ? void 0 : annotations[SNYK_ANNOTATION_TARGETS].split(',') : [];
        if (annotations === null || annotations === void 0 ? void 0 : annotations[SNYK_ANNOTATION_TARGETNAME]) {
            targetsArray.push(annotations === null || annotations === void 0 ? void 0 : annotations[SNYK_ANNOTATION_TARGETNAME]);
        }
        else if (annotations === null || annotations === void 0 ? void 0 : annotations[SNYK_ANNOTATION_TARGETID]) {
            targetsArray.push(annotations === null || annotations === void 0 ? void 0 : annotations[SNYK_ANNOTATION_TARGETID]);
        }
        if (targetsArray.length > 0) {
            const fullProjectByTargetList = await this.ProjectsListByTargets(orgId, Array.isArray(targetsArray) ? targetsArray : [...targetsArray]);
            completeProjectsList.push(...fullProjectByTargetList);
        }
        if (annotations === null || annotations === void 0 ? void 0 : annotations[SNYK_ANNOTATION_PROJECTIDS]) {
            const fullProjectByIdList = await this.ProjectsListByProjectIds(orgId, annotations === null || annotations === void 0 ? void 0 : annotations[SNYK_ANNOTATION_PROJECTIDS].split(','));
            completeProjectsList.push(...fullProjectByIdList);
        }
        if (annotations === null || annotations === void 0 ? void 0 : annotations[SNYK_ANNOTATION_EXCLUDE_PROJECTIDS]) {
            let idsToExclude = annotations === null || annotations === void 0 ? void 0 : annotations[SNYK_ANNOTATION_EXCLUDE_PROJECTIDS].split(',');
            idsToExclude = idsToExclude.filter(id => /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/.test(id));
            completeProjectsList = completeProjectsList.filter((project) => {
                return !idsToExclude.includes(project.id);
            });
        }
        return completeProjectsList;
    }
    async ProjectsListByTargets(orgId, repoName) {
        const TargetIdsArray = [];
        for (let i = 0; i < repoName.length; i++) {
            TargetIdsArray.push(`target_id=${await this.GetTargetId(orgId, repoName[i])}`);
        }
        const backendBaseUrl = await this.getApiUrl();
        let v3Headers = this.headers;
        v3Headers["Content-Type"] = "application/vnd.api+json";
        const projectsForTargetUrl = `${backendBaseUrl}/rest/orgs/${orgId}/projects?${TargetIdsArray.join('&')}&limit=100&version=2023-06-19~beta`;
        const response = await fetch(`${projectsForTargetUrl}`, {
            method: "GET",
            headers: v3Headers,
        });
        if (response.status >= 400 && response.status < 600) {
            throw new Error(`Error ${response.status} - Failed fetching Projects list snyk data`);
        }
        const jsonResponse = await response.json();
        return jsonResponse.data;
    }
    async ProjectsListByProjectIds(orgId, projectIdsArray) {
        const backendBaseUrl = await this.getApiUrl();
        let v3Headers = this.headers;
        v3Headers["Content-Type"] = "application/vnd.api+json";
        const projectsForTargetUrl = `${backendBaseUrl}/rest/orgs/${orgId}/projects?ids=${projectIdsArray.join('%2C')}&limit=100&version=2023-06-19~beta`;
        const response = await fetch(projectsForTargetUrl, {
            method: "GET",
            headers: v3Headers,
        });
        if (response.status >= 400 && response.status < 600) {
            throw new Error(`Error ${response.status} - Failed fetching Projects list snyk data`);
        }
        const jsonResponse = await response.json();
        return jsonResponse.data;
    }
    async GetTargetId(orgId, targetIdentifier) {
        var _a;
        if (targetIdentifier == '') {
            throw new Error(`Error - Unable to find repo name. Please add github.com/project-slug or snyk.io/target-id annotation`);
        }
        const backendBaseUrl = await this.getApiUrl();
        let v3Headers = this.headers;
        v3Headers["Content-Type"] = "application/vnd.api+json";
        let targetId;
        if (/^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/.test(targetIdentifier)) {
            targetId = targetIdentifier;
        }
        else {
            const targetsAPIUrl = `${backendBaseUrl}/rest/orgs/${orgId}/targets?displayName=${encodeURIComponent(targetIdentifier)}&version=2023-06-19~beta`;
            const targetResponse = await fetch(`${targetsAPIUrl}`, {
                method: "GET",
                headers: v3Headers,
            });
            if (targetResponse.status >= 400 && targetResponse.status < 600) {
                throw new Error(`Error ${targetResponse.status} - Failed fetching Targets list snyk data`);
            }
            const targetsList = await targetResponse.json();
            const targetsListData = targetsList.data;
            targetId = (_a = targetsListData.find(target => {
                return target.attributes.displayName == targetIdentifier;
            })) === null || _a === void 0 ? void 0 : _a.id;
            if (!targetId) {
                throw new Error(`Error - Failed finding Target snyk data for repo ${targetIdentifier}`);
            }
        }
        return targetId;
    }
    async GetDependencyGraph(orgName, projectId) {
        const backendBaseUrl = await this.getApiUrl();
        const apiUrl = `${backendBaseUrl}/v1/org/${orgName}/project/${projectId}/dep-graph`;
        const response = await fetch(`${apiUrl}`, {
            headers: this.headers,
        });
        if (response.status >= 400 && response.status < 600) {
            throw new Error(`Error ${response.status} - Failed fetching DepGraph snyk data`);
        }
        const jsonResponse = await response.json();
        return jsonResponse;
    }
}
//# sourceMappingURL=index.js.map